// Umami analytics (self-hosted) — this app's tracker.
//
// The tracker script is injected at runtime rather than hard-coded in
// index.html so the host/website id come from env per environment.
// `data-auto-track` is OFF: RouteTracker sends one pageview per SPA route
// change, which auto-track can't do reliably for a client-side router.

const rawHost = process.env.REACT_APP_UMAMI_HOST || '';
const UMAMI_HOST = rawHost.replace(/\/$/, '');
const UMAMI_WEBSITE_ID = process.env.REACT_APP_UMAMI_WEBSITE_ID;

// Identifies this app in the shared dashboard. Set per app via .env — this is
// the ONLY per-app difference in this file, so the tracker is drop-in reusable.
export const PRODUCT = process.env.REACT_APP_UMAMI_PRODUCT || '';

// ── Engaged-time tuning ─────────────────────────────────────────────────────
// How often engaged time is counted. Also the granularity of the measurement.
const TICK_MS = 1000;
// No input for this long means the person has stopped working, even if the tab
// is still open and focused. Deliberately generous: this app is timesheet and
// report heavy, so users genuinely read for a while without touching anything.
const IDLE_AFTER_MS = 120 * 1000;
// Time is reported at least this often, not only when the view ends, so a crash
// or power loss costs one interval instead of the whole visit.
const HEARTBEAT_SECONDS = 60;
// Below this a report is noise (instant redirect, bounced route) — not sent.
const MIN_REPORT_SECONDS = 1;

// Calls made before the tracker script finishes loading are buffered here and
// replayed on load, so an early identify()/pageview() is never dropped.
const pending = [];
let ready = false;

// Dev-only tracing. Every outgoing call and every engagement state change is
// logged so the tracker's behaviour can be followed in the console; silent in
// production builds.
const DEBUG = process.env.NODE_ENV === 'development';
const log = (label, data) => {
  if (!DEBUG) return;
  if (data === undefined) console.log(`%c[umami] ${label}`, 'color:#c6f24e');
  else console.log(`%c[umami] ${label}`, 'color:#c6f24e', data);
};

const runSafely = (fn) => {
  // Analytics must never break the app.
  try {
    fn();
  } catch (e) {
    if (DEBUG) console.warn('[umami] error', e);
  }
};

const flushQueue = () => {
  ready = true;
  log(`tracker loaded — flushing ${pending.length} queued call(s)`);
  while (pending.length) runSafely(pending.shift());
};

const enqueue = (fn) => {
  if (ready && window.umami) runSafely(fn);
  else {
    log('queued (tracker not loaded yet)');
    pending.push(fn);
  }
};

/** First path segment is the module; "/" is the dashboard. Mirrors the SQL. */
export const moduleOf = (path) => {
  const clean = (path || '/').split('?')[0];
  if (clean === '/' || clean === '') return 'dashboard';
  return clean.replace(/^\/+/, '').split('/')[0] || 'dashboard';
};

/* ── Engaged-time tracking ──────────────────────────────────────────────────
 * Time is COUNTED IN TICKS while the user is engaged, never derived from
 * elapsed wall-clock. That distinction is what makes this correct:
 *
 *   - A machine that sleeps or hibernates simply stops firing the interval,
 *     so the sleep contributes nothing. Subtracting timestamps would have
 *     credited the entire sleep to whatever module was open.
 *   - A system clock change or NTP jump cannot corrupt the total.
 *   - Idle, unfocused and hidden time are gated out before the tick counts,
 *     rather than measured and then subtracted.
 *
 * Engaged means all three of: the tab is visible, the window has focus, and
 * there has been user input recently.
 */
let currentPath = null;
let engagedSeconds = 0; // accrued on the current view, not yet reported
let sinceReport = 0; // engaged seconds since the last heartbeat
let lastInputAt = Date.now();
let hasFocus = true;
let tickTimer = null;

const isVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

const isEngaged = () =>
  Boolean(currentPath) &&
  isVisible() &&
  hasFocus &&
  Date.now() - lastInputAt < IDLE_AFTER_MS;

/** Sends the engaged time accrued so far, keeping the view open. */
const report = (reason = 'flush') => {
  const seconds = Math.round(sinceReport * 10) / 10;
  sinceReport = 0;
  if (!currentPath || seconds < MIN_REPORT_SECONDS) {
    if (currentPath) log(`report skipped (${reason}) — only ${seconds}s engaged`);
    return;
  }

  const path = currentPath;
  log(`SEND pageleave (${reason})`, { path, module: moduleOf(path), seconds });
  enqueue(() =>
    window.umami.track('pageleave', {
      path,
      module: moduleOf(path),
      seconds,
    })
  );
};

/** Reports the time and closes the view (navigation, logout, unload). */
const endCurrentView = (reason = 'view end') => {
  if (currentPath) log(`view ended: ${currentPath}`, { totalEngagedSeconds: engagedSeconds, reason });
  report(reason);
  currentPath = null;
  engagedSeconds = 0;
  sinceReport = 0;
};

const beginView = (path) => {
  currentPath = path;
  engagedSeconds = 0;
  sinceReport = 0;
  // A navigation is itself user activity — don't inherit a stale idle state.
  lastInputAt = Date.now();
  log(`view started: ${path}`, { module: moduleOf(path) });
};

// Logged once per transition rather than every tick, so the console shows when
// counting stops and starts without flooding it.
let wasEngaged = false;

const tick = () => {
  const engaged = isEngaged();

  if (engaged !== wasEngaged) {
    wasEngaged = engaged;
    if (engaged) {
      log(`counting RESUMED on ${currentPath}`);
    } else if (currentPath) {
      const idleMs = Date.now() - lastInputAt;
      log('counting PAUSED', {
        path: currentPath,
        visible: isVisible(),
        focused: hasFocus,
        idleSeconds: Math.round(idleMs / 1000),
        engagedSoFar: engagedSeconds,
      });
    }
  }

  if (!engaged) return;
  const step = TICK_MS / 1000;
  engagedSeconds += step;
  sinceReport += step;
  // Report periodically so a crash costs one interval, not the whole visit.
  if (sinceReport >= HEARTBEAT_SECONDS) report('heartbeat');
};

if (typeof document !== 'undefined') {
  tickTimer = setInterval(tick, TICK_MS);

  const markActive = () => {
    lastInputAt = Date.now();
  };
  // `passive` so scroll/touch handling is never delayed by analytics.
  ['mousemove', 'mousedown', 'keydown', 'scroll', 'click', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, markActive, { passive: true })
  );

  // Focus/blur catches alt-tabbing to another application and the case of the
  // tab sitting visible on a second monitor while the user works elsewhere —
  // neither of which fires visibilitychange.
  window.addEventListener('focus', () => {
    hasFocus = true;
    log('window focused');
    markActive();
  });
  window.addEventListener('blur', () => {
    hasFocus = false;
    log('window blurred (alt-tab / other app)');
    report('blur');
  });

  document.addEventListener('visibilitychange', () => {
    log(`visibility: ${document.visibilityState}`);
    if (document.visibilityState === 'hidden') {
      // The tab may never come back (closed, navigated away, phone locked).
      // visibilitychange is the last point where a request still reliably goes
      // out — beforeunload is skipped entirely on mobile. The path is kept so
      // that returning resumes the same view rather than inventing a new one.
      report('tab hidden');
    } else {
      markActive();
    }
  });

  // Desktop tab close / bfcache eviction, where visibilitychange may not fire.
  window.addEventListener('pagehide', () => endCurrentView('pagehide'));
}

const umami = {
  /**
   * One pageview for the given path. Stored in website_event.url_path, which
   * the dashboard splits into the module name. Also closes out the previous
   * view so its engaged time is recorded.
   */
  pageview(path) {
    const target = path || window.location.pathname;
    if (currentPath === target) {
      log(`pageview ignored (already on ${target})`);
      return; // same route re-render, not a new view
    }
    endCurrentView('navigation');

    log('SEND pageview', { url: target, module: moduleOf(target) });
    enqueue(() =>
      window.umami.track((props) => ({
        ...props,
        url: target,
        title: document.title,
      }))
    );

    beginView(target);
  },

  /** Named event with custom data (lands in event_data as key/value rows). */
  track(name, data) {
    log(`SEND event: ${name}`, data || {});
    enqueue(() => window.umami.track(name, data || {}));
  },

  /**
   * Attaches user properties to the current session (session_data table).
   * This is the Umami equivalent of posthog.identify — the dashboard joins
   * session_data to website_event to get country/user per pageview.
   */
  identify(id, props) {
    log('SEND identify', { id: String(id), ...(props || {}), product: PRODUCT });
    enqueue(() => window.umami.identify(String(id), { ...(props || {}), product: PRODUCT }));
  },

  /**
   * Umami exposes no reset(). Report the open view first so the last module the
   * user was on still gets its time, then clear the cached session keys so the
   * next event opens a fresh session instead of inheriting the old identity.
   */
  reset() {
    log('reset (logout) — flushing open view and clearing session cache');
    endCurrentView('logout');
    runSafely(() => {
      [window.localStorage, window.sessionStorage].forEach((store) => {
        if (!store) return;
        Object.keys(store)
          .filter((k) => k.startsWith('umami.'))
          .forEach((k) => store.removeItem(k));
      });
    });
  },

  /** Exposed for tests/debugging: current engaged seconds on the open view. */
  _engaged: () => ({ path: currentPath, engagedSeconds, engaged: isEngaged() }),
};

log('init', {
  host: UMAMI_HOST || '(missing)',
  websiteId: UMAMI_WEBSITE_ID || '(missing)',
  product: PRODUCT,
  idleAfterSeconds: IDLE_AFTER_MS / 1000,
  heartbeatSeconds: HEARTBEAT_SECONDS,
});

if (!UMAMI_HOST || !UMAMI_WEBSITE_ID) {
  log('DISABLED — REACT_APP_UMAMI_HOST / REACT_APP_UMAMI_WEBSITE_ID not set');
}

if (UMAMI_HOST && UMAMI_WEBSITE_ID && typeof document !== 'undefined') {
  const script = document.createElement('script');
  script.src = `${UMAMI_HOST}/script.js`;
  script.async = true;
  script.setAttribute('data-website-id', UMAMI_WEBSITE_ID);
  script.setAttribute('data-host-url', UMAMI_HOST);
  script.setAttribute('data-auto-track', 'false');
  script.onload = flushQueue;
  document.head.appendChild(script);
}

export default umami;
